import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { supabase } from './lib/supabase';

// Pages
import RecruiterLandingPage from './pages/RecruiterLandingPage';
import WorkerLandingPage from './pages/WorkerLandingPage';
import Login from './pages/Login';
import WorkerAuthPage from './pages/WorkerAuthPage';
import WorkerPortalPage from './pages/WorkerPortalPage';
import PrivacyPolicy from './PrivacyPolicy';
import TermsAndConditions from './TermsAndConditions';
import DeleteData from './DeleteData';
import PublicProfile from './pages/PublicProfile';
import RecruiterPosterPhoneExport from './pages/RecruiterPosterPhoneExport';
import DownloadPage from './pages/DownloadPage';
import AppDownloadRedirectPage from './pages/AppDownloadRedirectPage';

// Dashboard
import DashboardLayout from './components/dashboard/DashboardLayout';
import DashboardHome from './pages/dashboard/DashboardHome';
import Campaigns from './pages/dashboard/Campaigns';
import CreateCampaign from './pages/dashboard/CreateCampaign';
import CampaignDetails from './pages/dashboard/CampaignDetails';
import CreateJob from './pages/dashboard/CreateJob';
import Applicants from './pages/dashboard/Applicants';
import Interviews from './pages/dashboard/Interviews';
import Messages from './pages/dashboard/Messages';
import Settings from './pages/dashboard/Settings';
import WalletPage from './pages/dashboard/Wallet';
import Notifications from './pages/dashboard/Notifications';
import Reports from './pages/dashboard/Reports';
import MasterPanel from './pages/dashboard/MasterPanel';
import MasterApplicants from './pages/dashboard/MasterApplicants';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Ensure user is logged in before rendering route
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-jobisa-navy flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-jobisa-yellow border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const WorkerProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fffaf5] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/worker/login" replace />;
  }

  return <>{children}</>;
};

const RootRoute = () => {
  const { session, loading } = useAuth();
  const [shouldRedirectRecruiter, setShouldRedirectRecruiter] = useState(false);
  const [resolved, setResolved] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const exportScene = params.get('exportScene');
  const isRecruiterDomain = window.location.hostname.includes('recruiter.');
  const recruiterIntent = ['recruiter', 'role', 'audience', 'portal', 'type', 'site']
    .some((key) => String(params.get(key) || '').toLowerCase() === 'recruiter');

  useEffect(() => {
    let cancelled = false;

    const resolveRootRoute = async () => {
      if (exportScene === 'recruiter-poster-phone' || isRecruiterDomain || recruiterIntent) {
        if (!cancelled) {
          setShouldRedirectRecruiter(recruiterIntent);
          setResolved(true);
        }
        return;
      }

      if (loading) {
        return;
      }

      if (!session?.user) {
        if (!cancelled) {
          setShouldRedirectRecruiter(false);
          setResolved(true);
        }
        return;
      }

      try {
        const fallbackPhone = String(
          session.user.user_metadata?.phone || session.user.email?.replace(/@karya\.com$/i, '') || ''
        ).replace(/\D/g, '').slice(0, 10);

        let recruiterRecord = await supabase
          .from('recruiters')
          .select('id')
          .eq('id', session.user.id)
          .maybeSingle();

        if (!recruiterRecord.data && !recruiterRecord.error && fallbackPhone) {
          recruiterRecord = await supabase
            .from('recruiters')
            .select('id')
            .eq('phone', fallbackPhone)
            .maybeSingle();
        }

        if (!cancelled) {
          setShouldRedirectRecruiter(Boolean(recruiterRecord.data));
          setResolved(true);
        }
      } catch {
        if (!cancelled) {
          setShouldRedirectRecruiter(false);
          setResolved(true);
        }
      }
    };

    void resolveRootRoute();

    return () => {
      cancelled = true;
    };
  }, [exportScene, isRecruiterDomain, loading, recruiterIntent, session?.user]);

  if (exportScene === 'recruiter-poster-phone') {
    return <RecruiterPosterPhoneExport />;
  }

  if (isRecruiterDomain) {
    return <RecruiterLandingPage />;
  }

  if (shouldRedirectRecruiter) {
    return <Navigate to="/recruiter" replace />;
  }

  if (!resolved) {
    return (
      <div className="min-h-screen bg-[#fffaf5] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return <WorkerLandingPage />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <div className="font-sans text-jobisa-navy min-h-screen flex flex-col">
          <Routes>
            {/* DYNAMIC ROOT ROUTING */}
            <Route path="/" element={<RootRoute />} />

            {/* Explicit paths for local testing */}
            <Route path="/recruiter" element={<RecruiterLandingPage />} />
            <Route path="/worker" element={<WorkerLandingPage />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/delete-data" element={<DeleteData />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/download/:target" element={<AppDownloadRedirectPage />} />
            <Route path="/export/recruiter-poster-phone" element={<RecruiterPosterPhoneExport />} />
            <Route path="/login" element={<Login />} />
            <Route path="/worker/login" element={<WorkerAuthPage />} />
            <Route path="/worker/portal" element={<WorkerProtectedRoute><WorkerPortalPage /></WorkerProtectedRoute>} />
            <Route path="/profile/:id" element={<PublicProfile />} />

            {/* Protected Dashboard Routes */}
            <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="dashboard" element={<DashboardHome />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="campaigns/new" element={<CreateCampaign />} />
              <Route path="campaigns/:id" element={<CampaignDetails />} />
              <Route path="campaigns/:campaignId/jobs/new" element={<CreateJob />} />
              <Route path="candidates" element={<Applicants />} />
              <Route path="interviews" element={<Interviews />} />
              <Route path="messages" element={<Messages />} />
              <Route path="settings" element={<Settings />} />
              <Route path="wallet" element={<WalletPage />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="reports" element={<Reports />} />
              <Route path="master-panel" element={<MasterPanel />} />
              <Route path="master-applicants" element={<MasterApplicants />} />
            </Route>
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
